import React, { useState, useEffect, useMemo } from 'react';
import { CodelcoLayout } from '../../components/codelco/CodelcoLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Layers, Plus, Trash2, Send, Edit2, Info, Wand2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CodelcoPerfilesPage() {
    const [perfiles, setPerfiles] = useState([]);
    const [vehiculos, setVehiculos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [showApply, setShowApply] = useState(null);
    const [showEdit, setShowEdit] = useState(null);
    const [form, setForm] = useState({ nombre: '', descripcion: '', desde_vehiculo_id: '' });
    const [destinos, setDestinos] = useState([]);
    const [busy, setBusy] = useState(false);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

    const load = async () => {
        setLoading(true);
        try {
            const [pR, vR] = await Promise.all([
                fetch(`${API_URL}/api/codelco/perfiles`, { headers }),
                fetch(`${API_URL}/api/codelco/vehiculos`, { headers }),
            ]);
            if (pR.ok) setPerfiles(await pR.json());
            if (vR.ok) setVehiculos(await vR.json());
        } catch { /* ignore */ }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const crear = async () => {
        if (!form.nombre.trim()) { toast.error('Nombre requerido'); return; }
        setBusy(true);
        try {
            const r = await fetch(`${API_URL}/api/codelco/perfiles`, {
                method: 'POST', headers,
                body: JSON.stringify(form),
            });
            if (r.ok) {
                toast.success('Perfil creado');
                setShowCreate(false);
                setForm({ nombre: '', descripcion: '', desde_vehiculo_id: '' });
                load();
            } else {
                const d = await r.json();
                toast.error(d.detail || 'Error al crear');
            }
        } catch { toast.error('Error al crear'); }
        setBusy(false);
    };

    const guardarEdicion = async () => {
        if (!showEdit?.id) return;
        setBusy(true);
        try {
            const r = await fetch(`${API_URL}/api/codelco/perfiles/${showEdit.id}`, {
                method: 'PUT', headers,
                body: JSON.stringify({ nombre: showEdit.nombre, descripcion: showEdit.descripcion }),
            });
            if (r.ok) { toast.success('Perfil actualizado'); setShowEdit(null); load(); }
            else toast.error('Error al actualizar');
        } catch { toast.error('Error'); }
        setBusy(false);
    };

    const borrar = async (p) => {
        if (!window.confirm(`¿Eliminar perfil "${p.nombre}"? Los vehículos que lo usaban conservarán su mapeo (copia local).`)) return;
        try {
            const r = await fetch(`${API_URL}/api/codelco/perfiles/${p.id}`, { method: 'DELETE', headers });
            if (r.ok) { toast.success('Perfil eliminado'); load(); }
        } catch { toast.error('Error'); }
    };

    const aplicar = async () => {
        if (!destinos.length) { toast.error('Selecciona al menos un vehículo'); return; }
        setBusy(true);
        try {
            const r = await fetch(`${API_URL}/api/codelco/perfiles/${showApply.id}/aplicar`, {
                method: 'POST', headers,
                body: JSON.stringify({ vehiculos_destino: destinos }),
            });
            const d = await r.json();
            if (r.ok) {
                toast.success(`Perfil aplicado a ${d.aplicados} vehículo(s)`);
                setShowApply(null);
                setDestinos([]);
                load();
            } else toast.error(d.detail || 'Error');
        } catch { toast.error('Error al aplicar'); }
        setBusy(false);
    };

    const contarVehiculos = (pid) => vehiculos.filter(v => v.perfil_mapeo_id === pid).length;

    return (
        <CodelcoLayout>
            <div className="space-y-6" data-testid="codelco-perfiles-page">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-black tracking-tight flex items-center gap-2">
                            <Layers className="h-7 w-7 text-amber-500" /> Perfiles de mapeo GPS
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">Plantillas reutilizables de mapeo XML según el modelo del equipo GPS.</p>
                    </div>
                    <Button onClick={() => setShowCreate(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-nuevo-perfil">
                        <Plus className="h-4 w-4 mr-1" /> Nuevo perfil
                    </Button>
                </div>

                <div className="text-xs bg-blue-500/10 border border-blue-500/30 rounded p-3 flex gap-2">
                    <Info className="h-4 w-4 shrink-0 text-blue-600 mt-0.5" />
                    <div>
                        Al <b>aplicar</b> un perfil, el mapeo se copia a los vehículos seleccionados. Cambios posteriores al perfil <b>no se propagan automáticamente</b> (debes volver a aplicar). Esto te permite ajustar un vehículo sin afectar al resto.
                    </div>
                </div>

                {loading ? (
                    <Card><CardContent className="py-12 text-center text-muted-foreground">Cargando…</CardContent></Card>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {perfiles.map(p => {
                            const usedBy = contarVehiculos(p.id);
                            return (
                                <Card key={p.id} className="hover:border-amber-500/50 transition-colors" data-testid={`perfil-card-${p.nombre.replace(/\s/g,'-')}`}>
                                    <CardHeader>
                                        <CardTitle className="text-base flex items-start justify-between gap-2">
                                            <span className="leading-tight">{p.nombre}</span>
                                            {p.es_seed && <Badge variant="outline" className="text-xs shrink-0">Seed</Badge>}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <p className="text-xs text-muted-foreground min-h-[40px]">{p.descripcion || 'Sin descripción'}</p>
                                        <div className="flex items-center gap-2 text-xs">
                                            <Badge variant="secondary">{p.reglas_eventos?.length || 0} reglas</Badge>
                                            <Badge variant="secondary">{Object.keys(p.campos || {}).length} campos</Badge>
                                            <Badge className={usedBy > 0 ? 'bg-emerald-600' : 'bg-zinc-400'}>{usedBy} vehículos</Badge>
                                        </div>
                                        <div className="flex gap-1 pt-2 border-t">
                                            <Button size="sm" variant="outline" className="flex-1" onClick={() => { setShowApply(p); setDestinos([]); }} data-testid={`btn-aplicar-${p.id}`}>
                                                <Send className="h-3.5 w-3.5 mr-1" /> Aplicar
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => setShowEdit({ ...p })}>
                                                <Edit2 className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => borrar(p)} className="text-destructive">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                        <Link to={`/codelco/mapeo?perfil=${p.id}`} className="text-xs text-amber-600 hover:underline block text-center">
                                            <Wand2 className="h-3 w-3 inline mr-1" /> Editar mapeo detallado
                                        </Link>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal Crear */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Nuevo perfil de mapeo</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1">
                            <Label>Nombre *</Label>
                            <Input value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} placeholder="ej: Teltonika FMC920" data-testid="input-perfil-nombre" />
                        </div>
                        <div className="space-y-1">
                            <Label>Descripción</Label>
                            <Textarea value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} placeholder="Notas sobre este modelo de GPS, campos especiales, etc." />
                        </div>
                        <div className="space-y-1">
                            <Label>Tomar mapeo inicial desde un vehículo</Label>
                            <select className="w-full border rounded px-2 py-2 text-sm bg-background" value={form.desde_vehiculo_id} onChange={e => setForm({...form, desde_vehiculo_id: e.target.value})}>
                                <option value="">(Usar mapeo default del manual)</option>
                                {vehiculos.map(v => (<option key={v.id} value={v.id}>{v.patente} {v.imei && `· IMEI ${v.imei}`}</option>))}
                            </select>
                            <p className="text-xs text-muted-foreground">Si seleccionas un vehículo, su mapeo actual será la base de este perfil.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={crear} disabled={busy} className="bg-amber-600 hover:bg-amber-700">Crear perfil</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Editar (nombre/descripcion) */}
            <Dialog open={!!showEdit} onOpenChange={v => !v && setShowEdit(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Editar perfil</DialogTitle></DialogHeader>
                    {showEdit && (
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <Label>Nombre</Label>
                                <Input value={showEdit.nombre} onChange={e => setShowEdit({...showEdit, nombre: e.target.value})} />
                            </div>
                            <div className="space-y-1">
                                <Label>Descripción</Label>
                                <Textarea value={showEdit.descripcion || ''} onChange={e => setShowEdit({...showEdit, descripcion: e.target.value})} />
                            </div>
                            <p className="text-xs text-muted-foreground">Para editar campos/reglas detalladas usa la página "Mapeo XML".</p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEdit(null)}>Cancelar</Button>
                        <Button onClick={guardarEdicion} disabled={busy} className="bg-amber-600 hover:bg-amber-700">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal Aplicar */}
            <Dialog open={!!showApply} onOpenChange={v => !v && setShowApply(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Aplicar perfil "{showApply?.nombre}"</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded p-3">
                            <Info className="h-3.5 w-3.5 inline mr-1" />
                            Se copiará el mapeo del perfil a los vehículos seleccionados <b>sobrescribiendo</b> su mapeo previo.
                        </div>
                        <div className="flex justify-between items-center">
                            <Label>Vehículos destino</Label>
                            <div className="flex gap-2">
                                <Button variant="ghost" size="sm" onClick={() => setDestinos(vehiculos.map(v => v.id))}>Todos</Button>
                                <Button variant="ghost" size="sm" onClick={() => setDestinos([])}>Ninguno</Button>
                            </div>
                        </div>
                        <div className="max-h-72 overflow-y-auto border rounded divide-y">
                            {vehiculos.map(v => (
                                <label key={v.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50">
                                    <input type="checkbox" checked={destinos.includes(v.id)} onChange={() => setDestinos(prev => prev.includes(v.id) ? prev.filter(x=>x!==v.id) : [...prev, v.id])} className="h-4 w-4" />
                                    <div className="flex-1">
                                        <div className="font-mono font-bold text-sm">{v.patente}</div>
                                        <div className="text-xs text-muted-foreground">
                                            {v.marca || '—'} {v.perfil_mapeo_nombre && <Badge variant="outline" className="text-xs ml-1">Perfil actual: {v.perfil_mapeo_nombre}</Badge>}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <div className="text-xs text-muted-foreground">{destinos.length} seleccionado(s)</div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowApply(null)}>Cancelar</Button>
                        <Button onClick={aplicar} disabled={busy || !destinos.length} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-aplicar-confirmar">
                            <Send className="h-4 w-4 mr-1" /> Aplicar a {destinos.length}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </CodelcoLayout>
    );
}
